import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  AdminUser,
  CreateUserData,
  UpdateUserData,
  UpdatePasswordData,
  AdminTariff,
  SubscriptionDetailsResponse,
  BillingPeriod,
  SubscriptionStatus,
  CreateSubscriptionPayload,
  EditSubscriptionPayload,
  createAdmin,
  updateUser,
  updateUserPassword,
  deleteUser,
  getUserSubscription,
  getTariffs,
  createSubscriptionForUser,
  editSubscription,
  deleteSubscription,
  updateUserSpecialties,
  getAvailableSpecialties,
  getSpecialtiesByOrganization,
  getSpecialtiesForUser,
  getOrganizations,
  getCities,
  Specialty,
  SpecialtyWithSelection,
  Organization,
  City,
  ROLES,
  getUserDetails
} from '../../../utils/adminApiUtils';
import { getSpecialtyProfessions } from '../../../utils/apiUtils';
import CustomSelect from '../../../components/CustomSelect';

// Мемоизированный компонент для элемента специальности (оптимизация производительности)
const SpecialtyItem = React.memo<{
  specialty: SpecialtyWithSelection;
  onToggle: (id: number, checked: boolean) => void;
  isMobile?: boolean;
}>(({ specialty, onToggle, isMobile = false }) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onToggle(specialty.id, e.target.checked);
  }, [specialty.id, onToggle]);

  const className = isMobile
    ? `specialty-item-mobile ${specialty.is_selected ? 'selected' : ''}`
    : `specialty-item ${specialty.is_selected ? 'selected' : ''}`;

  return (
    <label key={specialty.id} className={className}>
      <input
        type="checkbox"
        checked={specialty.is_selected}
        onChange={handleChange}
      />
      <span className="specialty-content">
        <span className="specialty-name">
          <span className="specialty-id">#{specialty.id}</span> {specialty.name}
        </span>
        {specialty.organization && (
          <span className="specialty-org">
            {specialty.organization.name}
          </span>
        )}
      </span>
    </label>
  );
}, (prevProps, nextProps) => {
  // Кастомная функция сравнения для оптимизации - перерисовываем только если изменились нужные поля
  return (
    prevProps.specialty.id === nextProps.specialty.id &&
    prevProps.specialty.is_selected === nextProps.specialty.is_selected &&
    prevProps.specialty.name === nextProps.specialty.name &&
    prevProps.isMobile === nextProps.isMobile
  );
});

SpecialtyItem.displayName = 'SpecialtyItem';

const isActiveSpecialty = (s: Specialty): boolean => s.is_active !== false;

interface UserDialogProps {
  mode: 'create' | 'edit';
  user: AdminUser | null;
  onClose: () => void;
  onUserUpdated: (updatedUser?: AdminUser | null) => void;
}

const UserDialog: React.FC<UserDialogProps> = ({ mode, user, onClose, onUserUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    phone_number: '',
    first_name: '',
    last_name: '',
    email: '',
    specialty: '', // Для обратной совместимости при создании
    organization: 'LazyDoc',
    mis_city: '', // Город МИС
    role_id: 1,
    password: '',
    confirm_password: ''
  });

  // Состояние для множественных специальностей
  const [availableSpecialties, setAvailableSpecialties] = useState<SpecialtyWithSelection[]>([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Состояние для отслеживания изменений выбора (для реактивности)
  const [selectionVersion, setSelectionVersion] = useState(0);

  // Используем Set для хранения выбранных ID (максимально быстрый доступ O(1))
  const selectedSpecialtiesSet = useRef<Set<number>>(new Set());

  // Debounce для поиска (оптимизация производительности)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(specialtySearch);
    }, 150); // Задержка 150ms

    return () => clearTimeout(timer);
  }, [specialtySearch]);

  // Инициализируем Set при загрузке специальностей - обновляем при изменении данных
  useEffect(() => {
    const selectedIds = availableSpecialties
      .filter(s => s.is_selected)
      .map(s => s.id);
    const newSet = new Set(selectedIds);
    const hasChanged = selectedIds.length !== selectedSpecialtiesSet.current.size ||
      selectedIds.some(id => !selectedSpecialtiesSet.current.has(id)) ||
      Array.from(selectedSpecialtiesSet.current).some(id => !newSet.has(id));

    if (hasChanged) {
      selectedSpecialtiesSet.current = newSet;
      setSelectionVersion(prev => prev + 1);
    }
  }, [availableSpecialties]); // Обновляем при любом изменении массива

  // Мемоизация отфильтрованного списка специальностей для оптимизации
  // Используем debouncedSearch вместо specialtySearch
  const filteredSpecialties = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return availableSpecialties;
    }
    const searchLower = debouncedSearch.toLowerCase();
    return availableSpecialties.filter(specialty =>
      specialty.name.toLowerCase().includes(searchLower) ||
      specialty.id.toString().includes(searchLower)
    );
  }, [availableSpecialties, debouncedSearch]);

  // Создаем мемоизированный список с актуальным состоянием выбора из Set
  // Добавляем зависимость от selectionVersion для принудительного обновления при изменении выбора
  const specialtiesWithSelection = useMemo(() => {
    return filteredSpecialties.map(specialty => ({
      ...specialty,
      is_selected: selectedSpecialtiesSet.current.has(specialty.id)
    }));
  }, [filteredSpecialties, selectionVersion]);

  // Оптимизированный обработчик изменения выбора специальности
  // Обновляем только Set, не трогая основной массив - это максимально быстро
  const handleSpecialtyToggle = useCallback((specialtyId: number, checked: boolean) => {
    // Обновляем Set мгновенно
    if (checked) {
      selectedSpecialtiesSet.current.add(specialtyId);
    } else {
      selectedSpecialtiesSet.current.delete(specialtyId);
    }

    // Принудительно обновляем только нужный элемент в массиве для визуального обновления
    setAvailableSpecialties(prev => {
      const index = prev.findIndex(s => s.id === specialtyId);
      if (index === -1) return prev;

      // Если состояние уже совпадает, не обновляем
      if (prev[index].is_selected === checked) return prev;

      // Создаем новый массив только с измененным элементом
      const newArray = [...prev];
      newArray[index] = { ...newArray[index], is_selected: checked };
      return newArray;
    });

    // Принудительно обновляем selectionVersion для реактивности
    setSelectionVersion(prev => prev + 1);
  }, []);

  // Состояние для организаций
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  // Состояние для городов МИС
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Список профессий для поля specialty (из /specialties/professions)
  const [specialtyProfessions, setSpecialtyProfessions] = useState<string[]>([]);
  const [loadingProfessions, setLoadingProfessions] = useState(false);

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirm_password: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'subscriptions'>('profile');
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Загрузка доступных специальностей
  useEffect(() => {
    const fetchSpecialties = async () => {
      if (mode === 'edit' && user?.id) {
        // В режиме редактирования используем новый эндпоинт с флагами is_selected
        setLoadingSpecialties(true);
        try {
          const response = await getSpecialtiesForUser(user.id, {
            limit: 1000,
            sort_by: 'name',
            sort_order: 'asc',
          });
          setAvailableSpecialties(response.specialties.filter(isActiveSpecialty));
        } catch (err) {
          console.error('Ошибка загрузки специальностей для пользователя:', err);
          setAvailableSpecialties([]);
        } finally {
          setLoadingSpecialties(false);
        }
      } else {
        // Для создания пользователя загружаем все доступные специальности
        setLoadingSpecialties(true);
        try {
          const specialties = await getAvailableSpecialties();
          const specialtiesWithSelection: SpecialtyWithSelection[] = specialties
            .filter(isActiveSpecialty)
            .map(s => ({
              ...s,
              is_selected: false,
            }));
          setAvailableSpecialties(specialtiesWithSelection);
        } catch (err) {
          console.error('Ошибка загрузки специальностей:', err);
          setAvailableSpecialties([]);
        } finally {
          setLoadingSpecialties(false);
        }
      }
    };
    fetchSpecialties();
  }, [mode, user?.id]);

  // Загрузка организаций
  useEffect(() => {
    const fetchOrganizations = async () => {
      setLoadingOrganizations(true);
      try {
        const response = await getOrganizations({ limit: 1000, sort_by: 'name', sort_order: 'asc' });
        setOrganizations(response.organizations);
      } catch (err) {
        console.error('Ошибка загрузки организаций:', err);
      } finally {
        setLoadingOrganizations(false);
      }
    };
    fetchOrganizations();
  }, []);

  // Загрузка профессий для поля specialty
  useEffect(() => {
    setLoadingProfessions(true);
    getSpecialtyProfessions()
      .then(setSpecialtyProfessions)
      .catch(() => setSpecialtyProfessions([]))
      .finally(() => setLoadingProfessions(false));
  }, []);

  // Загрузка городов МИС
  useEffect(() => {
    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const response = await getCities();
        setCities(response.cities);
      } catch (err) {
        console.error('Ошибка загрузки городов:', err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    if (mode === 'edit' && user) {
      const roleId = user.role?.id || user.role_id || 1;
      setFormData({
        phone_number: user.phone_number || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        specialty: user.specialty || '', // Устаревшее поле для обратной совместимости
        organization: user.organization || '',
        mis_city: user.mis_city || '', // Город МИС
        role_id: roleId,
        password: '',
        confirm_password: ''
      });
    }
  }, [mode, user]);

  useEffect(() => {
    setActiveTab('profile');
    setShowPasswordSection(false);
    setShowDeleteConfirm(false);
  }, [mode, user?.id]);

  useEffect(() => {
    if (activeTab !== 'profile') {
      setShowPasswordSection(false);
      setShowDeleteConfirm(false);
    }
  }, [activeTab]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'Введите имя';
    } else if (formData.first_name.length < 3) {
      newErrors.first_name = 'Имя должно содержать минимум 3 символа';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Введите фамилию';
    } else if (formData.last_name.length < 3) {
      newErrors.last_name = 'Фамилия должна содержать минимум 3 символа';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Введите email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    // Телефон опционален, но если указан, должен быть валидным
    if (formData.phone_number && formData.phone_number.trim() && !/^\+\d{5,15}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Введите номер в международном формате (+1234567890)';
    }

    // Валидация специальностей не требуется для создания пользователя
    // Специальности можно назначить позже в режиме редактирования

    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Введите пароль';
      } else if (formData.password.length < 5) {
        newErrors.password = 'Пароль должен содержать минимум 5 символов';
      }

      if (!formData.confirm_password) {
        newErrors.confirm_password = 'Подтвердите пароль';
      } else if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Пароли не совпадают';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Специальный обработчик для изменения организации
  const handleOrganizationChange = async (value: string) => {
    const oldOrganization = formData.organization;
    setFormData(prev => ({ ...prev, organization: value }));

    // Сбрасываем mis_city если организация не Полимедика
    if (value.toLowerCase() !== 'полимедика') {
      setFormData(prev => ({ ...prev, mis_city: '' }));
    }

    // В режиме редактирования сразу обновляем профиль и перезагружаем специальности
    if (mode === 'edit' && user && oldOrganization !== value) {
      try {
        setLoadingSpecialties(true);

        // Обновляем организацию пользователя
        const updateData: UpdateUserData = {
          organization: value
        };

        // Если изменяем с Полимедики на другую организацию, очищаем mis_city
        if (oldOrganization.toLowerCase() === 'полимедика' && value.toLowerCase() !== 'полимедика') {
          updateData.mis_city = '';
        }

        await updateUser(user.id, updateData);

        // Перезагружаем специальности после обновления организации
        const response = await getSpecialtiesForUser(user.id, {
          limit: 1000,
          sort_by: 'name',
          sort_order: 'asc',
        });
        setAvailableSpecialties(response.specialties.filter(isActiveSpecialty));

      } catch (err) {
        console.error('Ошибка обновления организации:', err);
        enqueueSnackbar('Ошибка обновления организации', { variant: 'error' });
        // Откатываем изменение при ошибке
        setFormData(prev => ({ ...prev, organization: oldOrganization }));
      } finally {
        setLoadingSpecialties(false);
      }
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);

      const createData: CreateUserData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        organization: formData.organization,
        password: formData.password,
        confirm_password: formData.confirm_password,
        role_id: formData.role_id
      };

      // Добавляем specialty только если оно заполнено (для обратной совместимости)
      if (formData.specialty && formData.specialty.trim()) {
        createData.specialty = formData.specialty;
      }

      // Добавляем phone_number только если он заполнен
      if (formData.phone_number && formData.phone_number.trim()) {
        createData.phone_number = formData.phone_number;
      }

      await createAdmin(createData);
      // Для создания проще полностью перезагрузить список
      onUserUpdated();
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Ошибка создания пользователя' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm() || !user) return;

    try {
      setLoading(true);

      const updateData: UpdateUserData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        organization: formData.organization,
        role_id: formData.role_id
      };

      // Добавляем phone_number только если он заполнен
      if (formData.phone_number && formData.phone_number.trim()) {
        updateData.phone_number = formData.phone_number;
      }

      // Обновляем устаревшее строковое поле specialty, если оно заполнено
      if (formData.specialty && formData.specialty.trim()) {
        updateData.specialty = formData.specialty.trim();
      }

      // Добавляем mis_city если организация Полимедика
      if (formData.organization.toLowerCase() === 'полимедика' && formData.mis_city) {
        updateData.mis_city = formData.mis_city;
      }

      // Обновляем данные пользователя и получаем актуальный объект
      let finalUser: AdminUser = await updateUser(user.id, updateData);

      // Обновляем специальности пользователя отдельно (только для SuperAdmin)
      // Используем Set для быстрого получения выбранных ID
      const catalogIds = new Set(availableSpecialties.map(s => s.id));
      const selectedSpecialtyIds = Array.from(selectedSpecialtiesSet.current).filter(id =>
        catalogIds.has(id)
      );

      const currentSpecialtyIds = (user.specialties ?? [])
        .map(s => s.id)
        .filter(id => catalogIds.has(id))
        .sort();
      const newSpecialtyIds = [...selectedSpecialtyIds].sort();

      const specialtiesChanged = JSON.stringify(currentSpecialtyIds) !== JSON.stringify(newSpecialtyIds);

      if (specialtiesChanged) {
        try {
          const updatedUser = await updateUserSpecialties(user.id, selectedSpecialtyIds);
          finalUser = updatedUser;
          enqueueSnackbar('Специальности пользователя обновлены', { variant: 'success' });
        } catch (err) {
          console.error('Ошибка обновления специальностей:', err);
          enqueueSnackbar(
            err instanceof Error ? err.message : 'Не удалось обновить специальности пользователя',
            { variant: 'error' }
          );
        }
      }

      onUserUpdated(finalUser);
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Ошибка обновления пользователя' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!user) return;

    const newErrors: { [key: string]: string } = {};
    if (!passwordData.password) {
      newErrors.password = 'Введите новый пароль';
    } else if (passwordData.password.length < 5) {
      newErrors.password = 'Пароль должен содержать минимум 5 символов';
    }

    if (!passwordData.confirm_password) {
      newErrors.confirm_password = 'Подтвердите пароль';
    } else if (passwordData.password !== passwordData.confirm_password) {
      newErrors.confirm_password = 'Пароли не совпадают';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      await updateUserPassword(user.id, passwordData);
      setShowPasswordSection(false);
      setPasswordData({ password: '', confirm_password: '' });
      enqueueSnackbar('Пароль успешно обновлен', { variant: 'success' });
    } catch (error) {
      setErrors({ password: error instanceof Error ? error.message : 'Ошибка обновления пароля' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    try {
      setLoading(true);
      await deleteUser(user.id);
      // После удаления удобнее полностью перезагрузить список
      onUserUpdated();
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Ошибка удаления пользователя' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'create') {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  return (
    <div className="dialog-overlay" >
      <div className="user-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>{mode === 'create' ? 'Добавить пользователя' : 'Редактировать пользователя'}</h3>
          <button
            type="button"
            className="dialog-close-button"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        {mode === 'edit' && (
          <div className="user-dialog-tabs-wrapper">
            <div className="user-dialog-tabs">
              <button
                type="button"
                className={`admin-tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Профиль
              </button>
              <button
                type="button"
                className={`admin-tab-button ${activeTab === 'subscriptions' ? 'active' : ''}`}
                onClick={() => setActiveTab('subscriptions')}
              >
                Подписка
              </button>
            </div>
          </div>
        )}

        <div className="user-dialog-content-wrapper">
          <div className="user-dialog-content">

            {(mode === 'create' || activeTab === 'profile') && (
              <>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Имя *</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      className={errors.first_name ? 'error' : ''}
                    />
                    {errors.first_name && <div className="error-text">{errors.first_name}</div>}
                  </div>

                  <div className="form-group">
                    <label>Фамилия *</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      className={errors.last_name ? 'error' : ''}
                    />
                    {errors.last_name && <div className="error-text">{errors.last_name}</div>}
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <div className="error-text">{errors.email}</div>}
                  </div>
                  <div className="form-group">
                    <label>Телефон</label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      placeholder="+71234567890 (опционально)"
                      className={errors.phone_number ? 'error' : ''}
                    />
                    {errors.phone_number && <div className="error-text">{errors.phone_number}</div>}
                  </div>

                  {/* Блок специальностей для мобильных/планшетов (скрыт на больших экранах) */}
                  {mode === 'edit' && (
                    <div className="specialties-mobile">
                      <label>Специальности (список)</label>
                      <div className="specialties-search-mobile">
                        <input
                          type="text"
                          placeholder="Поиск специальностей..."
                          value={specialtySearch}
                          onChange={(e) => setSpecialtySearch(e.target.value)}
                          className="specialties-search-input"
                        />
                      </div>
                      {loadingSpecialties ? (
                        <div style={{ padding: '12px', color: '#666' }}>Загрузка специальностей...</div>
                      ) : specialtiesWithSelection.length > 0 ? (
                        <div className="specialties-list-mobile">
                          {specialtiesWithSelection.map((specialty) => (
                            <SpecialtyItem
                              key={specialty.id}
                              specialty={specialty}
                              onToggle={handleSpecialtyToggle}
                              isMobile={true}
                            />
                          ))}
                        </div>
                      ) : (
                        <div style={{ padding: '12px', color: '#666' }}>Нет доступных специальностей</div>
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Специальность</label>
                    {loadingProfessions ? (
                      <div style={{ padding: '12px', color: '#666' }}>Загрузка...</div>
                    ) : (
                      <CustomSelect
                        options={[
                          { value: '', label: mode === 'create' ? 'Опционально (можно назначить позже)' : 'Опционально' },
                          // Добавляем текущее значение, если оно не в списке (сохраняем при редактировании)
                          ...(formData.specialty && !specialtyProfessions.includes(formData.specialty)
                            ? [{ value: formData.specialty, label: formData.specialty }]
                            : []),
                          ...specialtyProfessions.map((p) => ({ value: p, label: p })),
                        ]}
                        value={formData.specialty}
                        onChange={(val) => handleInputChange('specialty', val.toString())}
                        placeholder={mode === 'create' ? 'Опционально (можно назначить позже)' : 'Опционально'}
                        className={`admin-style ${errors.specialty ? 'error' : ''}`}
                        searchable
                        searchPlaceholder="Поиск специальности..."
                      />
                    )}
                    {errors.specialty && <div className="error-text">{errors.specialty}</div>}
                  </div>

                  <div className="form-group">
                    <label>Организация</label>
                    {loadingOrganizations ? (
                      <div style={{ padding: '12px', color: '#666' }}>Загрузка организаций...</div>
                    ) : (
                      <CustomSelect
                        value={formData.organization}
                        onChange={(value) => handleOrganizationChange(value.toString())}
                        className="admin-style"
                        placeholder="Выберите организацию"
                        options={[
                          { value: '', label: 'Без организации' },
                          ...organizations.map((org) => ({
                            value: org.name,
                            label: org.name,
                          })),
                        ]}
                      />
                    )}
                  </div>

                  {/* Поле выбора города для организации Полимедика */}
                  {formData.organization.toLowerCase() === 'полимедика' && (
                    <div className="form-group">
                      <label>Город МИС</label>
                      {loadingCities ? (
                        <div style={{ padding: '12px', color: '#666' }}>Загрузка городов...</div>
                      ) : (
                        <CustomSelect
                          value={formData.mis_city}
                          onChange={(value) => handleInputChange('mis_city', value.toString())}
                          className="admin-style"
                          placeholder="Выберите город"
                          options={[
                            { value: '', label: 'Не выбран' },
                            ...cities.map((city) => ({
                              value: city.code,
                              label: city.name,
                            })),
                          ]}
                        />
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Роль *</label>
                    <CustomSelect
                      value={formData.role_id}
                      onChange={(value) => handleInputChange('role_id', parseInt(value.toString()))}
                      className="admin-style"
                      placeholder="Выберите роль"
                      options={Object.entries(ROLES).map(([id, role]) => ({
                        value: parseInt(id),
                        label: role.displayName
                      }))}
                    />
                  </div>

                  {mode === 'create' && (
                    <>
                      <div className="form-group">
                        <label>Пароль *</label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          className={errors.password ? 'error' : ''}
                        />
                        {errors.password && <div className="error-text">{errors.password}</div>}
                      </div>

                      <div className="form-group">
                        <label>Подтвердите пароль *</label>
                        <input
                          type="password"
                          value={formData.confirm_password}
                          onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                          className={errors.confirm_password ? 'error' : ''}
                        />
                        {errors.confirm_password && <div className="error-text">{errors.confirm_password}</div>}
                      </div>
                    </>
                  )}

                  {errors.submit && <div className="error-text">{errors.submit}</div>}

                  {mode === 'edit' && !showPasswordSection && !showDeleteConfirm && (
                    <div className="profile-actions">
                      <button
                        type="button"
                        className="change-password-btn"
                        onClick={() => setShowPasswordSection(true)}
                      >
                        Сменить пароль
                      </button>
                      <button
                        type="button"
                        className="danger-btn"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        Удалить
                      </button>
                    </div>
                  )}
                </form>

                {mode === 'edit' && showPasswordSection && (
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid #e8f2ff' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#1e6fff' }}>Смена пароля</h4>

                    <div className="form-group">
                      <label>Новый пароль *</label>
                      <input
                        type="password"
                        value={passwordData.password}
                        onChange={(e) => handlePasswordChange('password', e.target.value)}
                        className={errors.password ? 'error' : ''}
                      />
                      {errors.password && <div className="error-text">{errors.password}</div>}
                    </div>

                    <div className="form-group">
                      <label>Подтвердите пароль *</label>
                      <input
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => handlePasswordChange('confirm_password', e.target.value)}
                        className={errors.confirm_password ? 'error' : ''}
                      />
                      {errors.confirm_password && <div className="error-text">{errors.confirm_password}</div>}
                    </div>

                    <div className="dialog-actions">
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => {
                          setShowPasswordSection(false);
                          setPasswordData({ password: '', confirm_password: '' });
                          setErrors({});
                        }}
                      >
                        Отмена
                      </button>
                      <button
                        type="button"
                        className="primary"
                        onClick={handlePasswordUpdate}
                        disabled={loading}
                      >
                        {loading ? 'Обновление...' : 'Обновить пароль'}
                      </button>
                    </div>
                  </div>
                )}

                {showDeleteConfirm && (
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '2px solid #fed7d7' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#c53030' }}>Подтверждение удаления</h4>
                    <p style={{ margin: '0 0 16px 0' }}>
                      Вы действительно хотите удалить пользователя {user?.first_name} {user?.last_name}?
                      Это действие нельзя отменить.
                    </p>

                    <div className="dialog-actions">
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Отмена
                      </button>
                      <button
                        type="button"
                        className="danger"
                        onClick={handleDelete}
                        disabled={loading}
                      >
                        {loading ? 'Удаление...' : 'Удалить'}
                      </button>
                    </div>
                  </div>
                )}

              </>
            )}

            {mode === 'edit' && activeTab === 'subscriptions' && user && (
              <UserSubscriptionsManager user={user} onUserUpdated={onUserUpdated} />
            )}
          </div>

          {mode === 'edit' && activeTab === 'profile' && (
            <div className="user-specialties-panel">
              <h4 className="specialties-panel-title">Специальности</h4>
              <div className="specialties-search">
                <input
                  type="text"
                  placeholder="Поиск специальностей..."
                  value={specialtySearch}
                  onChange={(e) => setSpecialtySearch(e.target.value)}
                  className="specialties-search-input"
                />
              </div>
              {loadingSpecialties ? (
                <div className="specialties-loading">Загрузка специальностей...</div>
              ) : specialtiesWithSelection.length > 0 ? (
                <div className="specialties-list">
                  {specialtiesWithSelection.map((specialty) => (
                    <SpecialtyItem
                      key={specialty.id}
                      specialty={specialty}
                      onToggle={handleSpecialtyToggle}
                      isMobile={false}
                    />
                  ))}
                </div>
              ) : (
                <div className="specialties-empty">Нет доступных специальностей</div>
              )}
            </div>
          )}
        </div>

        {!showPasswordSection && !showDeleteConfirm && (mode === 'create' || activeTab === 'profile') && (
          <div className="dialog-actions-fixed">
            <button type="button" className="secondary" onClick={onClose}>
              Отмена
            </button>

            <button
              type="button"
              className="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Сохранение...' : (mode === 'create' ? 'Создать' : 'Сохранить')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDialog;

interface UserSubscriptionsManagerProps {
  user: AdminUser;
  onUserUpdated: (updatedUser?: AdminUser | null) => void;
}

const UserSubscriptionsManager: React.FC<UserSubscriptionsManagerProps> = ({ user, onUserUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetailsResponse | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState<boolean>(false);
  const [tariffs, setTariffs] = useState<AdminTariff[]>([]);

  const [createForm, setCreateForm] = useState<Omit<CreateSubscriptionPayload, 'user_id'>>({
    tariff_id: 0,
    billing_period: 'monthly',
    auto_renew: true,
    notes: '',
  });
  const [creating, setCreating] = useState<boolean>(false);

  const [editForm, setEditForm] = useState<EditSubscriptionPayload>({
    user_id: user.id,
    tariff_id: undefined,
    billing_period: undefined,
    status: undefined,
    protocol_limit_total: undefined,
    protocols_balance: undefined,
    notes: undefined,
    update_protocol_limit: false,
    reset_balance_to_total: false,
  });

  const fetchSubscription = useCallback(async () => {
    setSubscriptionLoading(true);
    setError(null);
    try {
      const data = await getUserSubscription(user.id);
      console.log('Получена подписка:', data);
      setSubscription(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось загрузить подписку пользователя';
      console.error('Ошибка загрузки подписки:', err);
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setSubscriptionLoading(false);
      setLoading(false);
    }
  }, [user.id, enqueueSnackbar]);

  useEffect(() => {
    void fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getTariffs();
        if (mounted) {
          setTariffs(data);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Не удалось загрузить тарифы';
        enqueueSnackbar(message, { variant: 'error' });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar]);

  // Обновляем формы при загрузке подписки
  useEffect(() => {
    if (subscription?.subscription) {
      const sub = subscription.subscription;
      setEditForm({
        user_id: user.id,
        tariff_id: sub.tariff_id,
        billing_period: sub.billing_period,
        status: sub.status,
        protocol_limit_total: sub.protocol_limit_total,
        protocols_balance: sub.protocols_balance,
        notes: sub.notes || undefined,
        update_protocol_limit: false,
        reset_balance_to_total: false,
      });
    }
  }, [subscription, user.id]);

  const handleCreateSubscription = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!createForm.tariff_id) {
      enqueueSnackbar('Выберите тариф', { variant: 'error' });
      return;
    }

    setCreating(true);
    try {
      const payload: CreateSubscriptionPayload = {
        user_id: user.id,
        tariff_id: createForm.tariff_id,
        billing_period: createForm.billing_period,
        auto_renew: createForm.auto_renew,
        notes: createForm.notes?.trim() || undefined,
      };
      await createSubscriptionForUser(payload);
      enqueueSnackbar('Подписка создана', { variant: 'success' });
      setCreateForm({
        tariff_id: 0,
        billing_period: 'monthly',
        auto_renew: true,
        notes: '',
      });
      await fetchSubscription();
      try {
        const updatedUser = await getUserDetails(user.id);
        onUserUpdated(updatedUser);
      } catch {
        // Если не удалось получить обновлённого пользователя, просто не трогаем список
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось создать подписку';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleEditSubscription = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!subscription?.subscription) return;

    const sub = subscription.subscription;
    const payload: EditSubscriptionPayload = {
      user_id: user.id,
      tariff_id: editForm.tariff_id !== sub.tariff_id ? editForm.tariff_id : undefined,
      billing_period: editForm.billing_period !== sub.billing_period ? editForm.billing_period : undefined,
      status: editForm.status !== sub.status ? editForm.status : undefined,
      protocol_limit_total: editForm.protocol_limit_total !== sub.protocol_limit_total ? editForm.protocol_limit_total : undefined,
      protocols_balance: editForm.protocols_balance !== sub.protocols_balance ? editForm.protocols_balance : undefined,
      notes: editForm.notes !== (sub.notes || undefined) ? editForm.notes : undefined,
      update_protocol_limit: editForm.update_protocol_limit,
      reset_balance_to_total: editForm.reset_balance_to_total,
    };

    // Удаляем undefined поля
    Object.keys(payload).forEach(key => {
      if (payload[key as keyof EditSubscriptionPayload] === undefined) {
        delete payload[key as keyof EditSubscriptionPayload];
      }
    });

    if (Object.keys(payload).length <= 1) { // только user_id
      enqueueSnackbar('Нет изменений для сохранения', { variant: 'info' });
      return;
    }

    try {
      await editSubscription(payload);
      enqueueSnackbar('Подписка обновлена', { variant: 'success' });
      await fetchSubscription();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось обновить подписку';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleDeleteSubscription = async () => {
    if (!subscription) return;
    if (!window.confirm('Вы действительно хотите удалить подписку? Это действие нельзя отменить.')) {
      return;
    }
    try {
      await deleteSubscription(user.id);
      enqueueSnackbar('Подписка удалена', { variant: 'success' });
      setSubscription(null);
      await fetchSubscription();
      try {
        const updatedUser = await getUserDetails(user.id);
        onUserUpdated(updatedUser);
      } catch {
        // Если не удалось получить обновлённого пользователя, просто не трогаем список
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось удалить подписку';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <section className="user-subscriptions-section">
      {loading ? (
        <div className="loading small">Загрузка подписки...</div>
      ) : error ? (
        <div className="error-banner">
          <span>{error}</span>
          <button type="button" onClick={() => void fetchSubscription()}>
            Повторить
          </button>
        </div>
      ) : (
        <div className="subscriptions-content">
          <div className="subscriptions">
            {!subscription ? (
              <div className="create-subscription-card">
                <h3>Создать подписку</h3>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                  У пользователя нет активной подписки. Создайте новую подписку.
                </p>
                <form onSubmit={handleCreateSubscription}>
                  <label>
                    Тариф *
                    <select
                      value={createForm.tariff_id}
                      onChange={(event) =>
                        setCreateForm((prev) => ({ ...prev, tariff_id: Number(event.target.value) }))
                      }
                    >
                      <option value={0}>Выберите тариф</option>
                      {tariffs.map((tariff) => (
                        <option key={tariff.id} value={tariff.id}>
                          {tariff.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Период оплаты *
                    <select
                      value={createForm.billing_period}
                      onChange={(event) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          billing_period: event.target.value as BillingPeriod,
                        }))
                      }
                    >
                      <option value="monthly">Ежемесячно</option>
                      <option value="yearly">Ежегодно</option>
                    </select>
                  </label>

                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={createForm.auto_renew}
                      onChange={(event) =>
                        setCreateForm((prev) => ({ ...prev, auto_renew: event.target.checked }))
                      }
                    />
                    <span>Автопродление</span>
                  </label>

                  <label>
                    Заметки администратора
                    <textarea
                      rows={3}
                      value={createForm.notes}
                      onChange={(event) =>
                        setCreateForm((prev) => ({ ...prev, notes: event.target.value }))
                      }
                      placeholder="Комментарий к подписке"
                    />
                  </label>

                  <button type="submit" className="primary" disabled={creating}>
                    {creating ? 'Создание...' : 'Создать подписку'}
                  </button>
                </form>
              </div>
            ) : (
              <>
                <div className="subscription-details-card">
                  <h3>Детали подписки</h3>
                  {subscriptionLoading ? (
                    <div className="loading small">Загрузка деталей...</div>
                  ) : subscription?.subscription ? (
                    <>
                      <div className="details-block">
                        <div className="details-row">
                          <span className="label">Статус:</span>
                          <span className={`status-dot ${subscription.subscription.status}`}>
                            {subscription.subscription.status}
                          </span>
                        </div>
                        <div className="details-row">
                          <span className="label">Лимит:</span>
                          <span>
                            {subscription.subscription.protocols_balance} /{' '}
                            {subscription.subscription.protocol_limit_total}
                          </span>
                        </div>
                        <div className="details-row">
                          <span className="label">Следующее списание:</span>
                          <span>
                            {subscription.subscription.next_billing_date
                              ? new Date(subscription.subscription.next_billing_date).toLocaleString('ru-RU')
                              : '—'}
                          </span>
                        </div>
                        <div className="details-row">
                          <span className="label">Автопродление:</span>
                          <span>{subscription.subscription.auto_renew ? 'Включено' : 'Выключено'}</span>
                        </div>
                        <div className="details-row">
                          <span className="label">Комментарий:</span>
                          <span>{subscription.subscription.notes || '—'}</span>
                        </div>
                      </div>

                      <div className="details-forms">
                        <form onSubmit={handleEditSubscription}>
                          <label>
                            Тариф
                            <select
                              value={editForm.tariff_id ?? 0}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  tariff_id: Number(event.target.value) || undefined,
                                }))
                              }
                            >
                              <option value={0}>Выберите тариф</option>
                              {tariffs.map((tariff) => (
                                <option key={tariff.id} value={tariff.id}>
                                  {tariff.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Период оплаты
                            <select
                              value={editForm.billing_period ?? ''}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  billing_period: (event.target.value as BillingPeriod) || undefined,
                                }))
                              }
                            >
                              <option value="">Не изменять</option>
                              <option value="monthly">Ежемесячно</option>
                              <option value="yearly">Ежегодно</option>
                            </select>
                          </label>
                          <label>
                            Статус
                            <select
                              value={editForm.status ?? ''}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  status: (event.target.value as SubscriptionStatus) || undefined,
                                }))
                              }
                            >
                              <option value="">Не изменять</option>
                              <option value="active">Активна</option>
                              <option value="expired">Истекла</option>
                              <option value="canceled">Отменена</option>
                            </select>
                          </label>
                          <div className="grid-two">
                            <label>
                              Общий лимит протоколов
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={editForm.protocol_limit_total ?? ''}
                                onChange={(event) =>
                                  setEditForm((prev) => ({
                                    ...prev,
                                    protocol_limit_total: event.target.value
                                      ? Number(event.target.value)
                                      : undefined,
                                  }))
                                }
                                placeholder="Не изменять"
                              />
                            </label>
                            <label>
                              Текущий баланс протоколов
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={editForm.protocols_balance ?? ''}
                                onChange={(event) =>
                                  setEditForm((prev) => ({
                                    ...prev,
                                    protocols_balance: event.target.value
                                      ? Number(event.target.value)
                                      : undefined,
                                  }))
                                }
                                placeholder="Не изменять"
                              />
                            </label>
                          </div>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={editForm.update_protocol_limit ?? false}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  update_protocol_limit: event.target.checked,
                                }))
                              }
                            />
                            <span>Синхронизировать лимит протоколов с тарифом</span>
                          </label>
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              checked={editForm.reset_balance_to_total ?? false}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  reset_balance_to_total: event.target.checked,
                                }))
                              }
                            />
                            <span>Сбросить баланс до лимита</span>
                          </label>
                          <label>
                            Заметки
                            <textarea
                              rows={2}
                              value={editForm.notes ?? ''}
                              onChange={(event) =>
                                setEditForm((prev) => ({
                                  ...prev,
                                  notes: event.target.value || undefined,
                                }))
                              }
                              placeholder="Комментарий к подписке"
                            />
                          </label>
                          <div className="buttons-inline">
                            <button type="submit" className="secondary">
                              Сохранить изменения
                            </button>
                            <button
                              type="button"
                              className="danger-btn"
                              onClick={handleDeleteSubscription}
                            >
                              Удалить подписку
                            </button>
                          </div>
                        </form>
                      </div>
                    </>
                  ) : (
                    <div>
                      <h4>Детали подписки</h4>
                      <p>Подписка не найдена</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};